import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Package, Split, Truck, Scale, Download, FileSpreadsheet, FileText } from "lucide-react";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "all");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHubs();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [selectedHub]);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {};
      // For staff/managers, always use their assigned hub
      if (user?.hub_id) {
        params.hub_id = user.hub_id;
      } else if (selectedHub && selectedHub !== "all") {
        params.hub_id = selectedHub;
      }
      const response = await axios.get(`${API}/reports/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    { title: "Total Inward", value: `${stats?.total_inward?.toFixed(1) || 0} kg`, count: stats?.inward_count || 0, icon: Package, color: "text-chart-1" },
    { title: "Total Segregation", value: `${stats?.total_segregation?.toFixed(1) || 0} kg`, count: stats?.segregation_count || 0, icon: Split, color: "text-chart-2" },
    { title: "Total Dispatch", value: `${stats?.total_dispatch?.toFixed(1) || 0} kg`, count: stats?.dispatch_count || 0, icon: Truck, color: "text-chart-3" },
    { title: "Balance Stock", value: `${stats?.balance_stock?.toFixed(1) || 0} kg`, count: "-", icon: Scale, color: "text-chart-4" },
  ];

  const getHubName = () => {
    if (user?.hub_id) {
      const hub = hubs.find(h => h.id === user.hub_id);
      return hub?.name || "Your Hub";
    }
    if (selectedHub === "all") return "All Hubs";
    const hub = hubs.find(h => h.id === selectedHub);
    return hub?.name || "Selected Hub";
  };

  const flowData = [
    { stage: "Inward", weight: stats?.total_inward || 0 },
    { stage: "Segregation", weight: stats?.total_segregation || 0 },
    { stage: "Dispatch", weight: stats?.total_dispatch || 0 },
  ];

  const downloadCSV = async (type) => {
    try {
      const params = (selectedHub && selectedHub !== "all") ? { hub_id: selectedHub } : {};
      const response = await axios.get(`${API}/reports/download/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      saveAs(blob, `${type}_report_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`${type} CSV downloaded successfully`);
    } catch (error) {
      console.error(`Error downloading ${type} report:`, error);
      toast.error(`Failed to download ${type} CSV`);
    }
  };

  const downloadExcel = async (type) => {
    try {
      const params = (selectedHub && selectedHub !== "all") ? { hub_id: selectedHub } : {};
      let response;
      
      if (type === 'inward') {
        response = await axios.get(`${API}/inward`, { headers: { Authorization: `Bearer ${token}` }, params });
      } else if (type === 'segregation') {
        response = await axios.get(`${API}/segregation`, { headers: { Authorization: `Bearer ${token}` }, params });
      } else if (type === 'dispatch') {
        response = await axios.get(`${API}/dispatch`, { headers: { Authorization: `Bearer ${token}` }, params });
      } else if (type === 'staff') {
        response = await axios.get(`${API}/staff`, { headers: { Authorization: `Bearer ${token}` }, params });
      }

      const data = response.data;
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, type);
      XLSX.writeFile(wb, `${type}_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`${type} Excel downloaded successfully`);
    } catch (error) {
      console.error(`Error downloading ${type} Excel:`, error);
      toast.error(`Failed to download ${type} Excel`);
    }
  };

  const downloadPDF = async (type) => {
    try {
      const params = (selectedHub && selectedHub !== "all") ? { hub_id: selectedHub } : {};
      const response = await axios.get(`${API}/reports/download/${type}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, `${type}_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`${type} PDF downloaded successfully`);
    } catch (error) {
      console.error(`Error downloading ${type} PDF:`, error);
      toast.error(`Failed to download ${type} PDF`);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Reports</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">
            <span className="font-semibold text-primary">{getHubName()}</span>
            {" • Comprehensive analytics and insights"}
          </p>
        </div>
        {!user?.hub_id && hubs.length > 0 && (
          <div className="w-full sm:w-48 lg:w-64">
            <Label htmlFor="hub-select" className="text-sm">Select Hub</Label>
            <Select value={selectedHub} onValueChange={setSelectedHub}>
              <SelectTrigger id="hub-select" data-testid="reports-hub-select">
                <SelectValue placeholder="Select Hub" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hubs</SelectItem>
                {hubs.map((hub) => (
                  <SelectItem key={hub.id} value={hub.id}>
                    {hub.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow duration-300" data-testid={`report-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 p-3 sm:p-4 lg:p-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider truncate">
                {stat.title}
              </CardTitle>
              <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.color} flex-shrink-0`} />
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
              <div className="text-lg sm:text-2xl lg:text-3xl font-bold font-heading">
                {loading ? "..." : stat.value}
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                {stat.count !== "-" ? `${stat.count} entries` : "Net balance"}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Admin Download Section */}
      {user?.role === 'admin' && (
        <Card className="shadow-lg border-border/50">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Download Reports (Admin Only)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {['inward', 'segregation', 'dispatch', 'staff'].map((type) => (
                <Card key={type} className="border-2">
                  <CardContent className="p-3 sm:p-4 lg:pt-6">
                    <h3 className="font-semibold text-sm sm:text-base lg:text-lg capitalize mb-3">{type} Data</h3>
                    <div className="space-y-2">
                      <Button 
                        onClick={() => downloadExcel(type)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm"
                        size="sm"
                        data-testid={`download-excel-${type}`}
                      >
                        <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Excel
                      </Button>
                      <Button 
                        onClick={() => downloadPDF(type)}
                        className="w-full bg-red-600 hover:bg-red-700 text-xs sm:text-sm"
                        size="sm"
                        data-testid={`download-pdf-${type}`}
                      >
                        <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        PDF
                      </Button>
                      <Button 
                        onClick={() => downloadCSV(type)}
                        variant="outline"
                        className="w-full text-xs sm:text-sm"
                        size="sm"
                        data-testid={`download-csv-${type}`}
                      >
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="shadow-lg border-border/50">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Material Flow</CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 lg:p-6 pt-0">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={flowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="weight" fill="hsl(158, 64%, 52%)" name="Weight (kg)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-border/50">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Process Efficiency</CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 lg:p-6 pt-0">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={flowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="weight" stroke="hsl(175, 84%, 32%)" strokeWidth={3} name="Weight (kg)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card className="shadow-lg border-border/50">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Segregation Rate</p>
              <p className="text-xl sm:text-2xl font-bold font-heading text-primary">
                {stats?.total_inward > 0 ? ((stats.total_segregation / stats.total_inward) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-accent/10 to-accent/5">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Dispatch Rate</p>
              <p className="text-xl sm:text-2xl font-bold font-heading text-accent">
                {stats?.total_segregation > 0 ? ((stats.total_dispatch / stats.total_segregation) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-secondary/10 to-secondary/5">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Stock Retention</p>
              <p className="text-xl sm:text-2xl font-bold font-heading text-secondary">
                {stats?.total_segregation > 0 ? ((stats.balance_stock / stats.total_segregation) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
